/**
 * Tech-debt #4 — Smart Charging family: SetChargingProfile / ClearChargingProfile
 * (persisted to the local ChargingProfileRepository), plus the v2.0.1
 * GetCompositeSchedule (single resolved period) and GetChargingProfiles
 * (paginated ReportChargingProfiles).
 */
import { errorMessage } from '@/lib/errorMessage';
import { computeCompositeSchedule } from '@/domain/ChargingProfileResolver';
import { OcppV201Adapter } from '@/protocol/v201/OcppV201Adapter';
import { paginateReportChargingProfiles } from '@/protocol/v201/actions/smartChargingReports';
import {
  ChargingProfile,
  parseSetChargingProfilePayload,
} from '@/protocol/v16/actions/setChargingProfile';
import { useStationStore } from '../useStationStore';
import type { CsmsCtx, CsmsEvent } from './context';
import { chargingProfileRepo, maxReportItems, varValuesRepo } from './shared';

export async function handleClearChargingProfile(
  event: CsmsEvent<'ClearChargingProfile'>,
  ctx: CsmsCtx,
): Promise<void> {
  // BUG-026 / Session 8f.15 — per OCPP 1.6J §5.5. Up to 4 optional filter
  // fields (id, connectorId, chargingProfilePurpose, stackLevel). Empty filter
  // = clear all. Reply Accepted when ≥ 1 row deleted, Unknown when 0 matched.
  const all = await chargingProfileRepo.byStation(ctx.stationId);
  const matches = all.filter((p) => {
    if (event.id !== undefined && p.id !== event.id) return false;
    if (event.connectorId !== undefined && p.connectorId !== event.connectorId) return false;
    if (event.chargingProfilePurpose !== undefined && p.purpose !== event.chargingProfilePurpose) {
      return false;
    }
    if (event.stackLevel !== undefined && p.stackLevel !== event.stackLevel) return false;
    return true;
  });
  for (const p of matches) {
    await chargingProfileRepo.delete(ctx.stationId, p.id);
  }
  ctx.dispatcher.reply(ctx.messageId, { status: matches.length > 0 ? 'Accepted' : 'Unknown' });
}

export async function handleSetChargingProfile(
  event: CsmsEvent<'SetChargingProfile'>,
  ctx: CsmsCtx,
): Promise<void> {
  // BUG-014 — parse → validate → persist → Accepted. Failure → Rejected.
  const snap = parseSetChargingProfilePayload(event.connectorId, event.csChargingProfile);
  if (!snap) {
    ctx.dispatcher.reply(ctx.messageId, { status: 'Rejected' });
    return;
  }
  try {
    new ChargingProfile(snap); // constructor enforces invariants
  } catch {
    ctx.dispatcher.reply(ctx.messageId, { status: 'Rejected' });
    return;
  }
  try {
    await chargingProfileRepo.upsert(ctx.stationId, snap);
  } catch {
    ctx.dispatcher.reply(ctx.messageId, { status: 'Rejected' });
    return;
  }
  ctx.dispatcher.reply(ctx.messageId, { status: 'Accepted' });
}

export async function handleGetCompositeSchedule(
  event: CsmsEvent<'GetCompositeSchedule'>,
  ctx: CsmsCtx,
): Promise<void> {
  const { adapter, dispatcher, stationId, messageId } = ctx;
  // Flatten the installed profiles into a real composite schedule (min of the
  // operating profile and the ChargePointMax ceiling, over the requested
  // duration). Works for both protocols — v1.6 previously always Rejected.
  const profiles = await chargingProfileRepo.byStation(stationId);
  const conn = useStationStore.getState().connectors.get(event.connectorId);
  const txId = conn?.activeTransactionId != null ? Number(conn.activeTransactionId) : null;
  const rateUnit = event.chargingRateUnit === 'A' ? 'A' : 'W';
  const composite = computeCompositeSchedule({
    profiles,
    connectorId: Number(event.connectorId),
    txId,
    nowMs: Date.now(),
    txStartedAtMs: null,
    durationSec: event.duration,
    rateUnit,
  });
  if (!composite) {
    dispatcher.reply(messageId, { status: 'Rejected' });
    return;
  }
  const periods = composite.periods.map((p) => ({ startPeriod: p.startPeriod, limit: p.limit }));
  const scheduleStart = new Date().toISOString();
  if (adapter instanceof OcppV201Adapter) {
    dispatcher.reply(messageId, {
      status: 'Accepted',
      schedule: {
        evseId: conn ? Number(conn.evseId) : 0,
        duration: event.duration,
        scheduleStart,
        chargingRateUnit: composite.chargingRateUnit,
        chargingSchedulePeriod: periods,
      },
    });
    return;
  }
  // OCPP 1.6J GetCompositeScheduleResponse shape (connectorId + chargingSchedule).
  dispatcher.reply(messageId, {
    status: 'Accepted',
    connectorId: Number(event.connectorId),
    scheduleStart,
    chargingSchedule: {
      duration: event.duration,
      chargingRateUnit: composite.chargingRateUnit,
      chargingSchedulePeriod: periods,
    },
  });
}

export async function handleGetChargingProfiles(
  event: CsmsEvent<'GetChargingProfiles'>,
  ctx: CsmsCtx,
): Promise<void> {
  const { adapter, dispatcher, stationId, messageId } = ctx;
  // Session 19c — v2.0.1-only. Match installed profiles, reply
  // Accepted/NoProfiles, then stream paginated ReportChargingProfiles.
  if (!(adapter instanceof OcppV201Adapter)) {
    dispatcher.replyError(messageId, 'NotImplemented', 'GetChargingProfiles is v2.0.1-only', {});
    return;
  }
  const allCP = await chargingProfileRepo.byStation(stationId);
  const ids = event.chargingProfileId;
  const matched = allCP.filter((p) => {
    if (event.connectorId !== 0 && p.connectorId !== 0 && p.connectorId !== Number(event.connectorId)) {
      return false;
    }
    if (event.chargingProfilePurpose !== undefined && p.purpose !== event.chargingProfilePurpose) {
      return false;
    }
    if (event.stackLevel !== undefined && p.stackLevel !== event.stackLevel) return false;
    if (ids !== undefined && ids.length > 0 && !ids.includes(Number(p.id))) return false;
    return true;
  });
  if (matched.length === 0) {
    dispatcher.reply(messageId, { status: 'NoProfiles' });
    return;
  }
  dispatcher.reply(messageId, { status: 'Accepted' });
  const rowsCP = await varValuesRepo.byStation(stationId);
  const connCP =
    event.connectorId !== 0
      ? useStationStore.getState().connectors.get(event.connectorId)
      : undefined;
  const evseIdCP = connCP ? Number(connCP.evseId) : 0;
  const pages = paginateReportChargingProfiles(
    event.requestId,
    evseIdCP,
    'CSO',
    matched,
    maxReportItems(rowsCP),
  );
  try {
    for (const page of pages) {
      await adapter.reportChargingProfiles(page).response;
    }
  } catch (err) {
    useStationStore.setState({ lastError: errorMessage(err) });
  }
}
